import QRCode from 'qrcode';

/**
 * Thermal Receipt Printer Service
 * 
 * Communicates with ESC/POS compatible thermal printers (e.g. CP-306)
 * via the WebUSB API. Uses native ESC/POS commands for crisp text printing.
 * 
 * Works in Chrome/Edge/Chromium browsers only.
 */

const CHAR_WIDTH = 48; // 80mm paper = ~48 chars per line with default Font A

// ─── ESC/POS Command Constants ───────────────────────────────────────────────

const ESC = 0x1B;
const GS = 0x1D;
const LF = 0x0A;

const COMMANDS = {
    INIT: [ESC, 0x40],
    CODEPAGE_CP437: [ESC, 0x74, 0x00],
    ALIGN_LEFT: [ESC, 0x61, 0x00],
    ALIGN_CENTER: [ESC, 0x61, 0x01],
    ALIGN_RIGHT: [ESC, 0x61, 0x02],
    BOLD_ON: [ESC, 0x45, 0x01],
    BOLD_OFF: [ESC, 0x45, 0x00],
    SIZE_NORMAL: [GS, 0x21, 0x00],
    SIZE_DOUBLE_HEIGHT: [GS, 0x21, 0x01],
    SIZE_DOUBLE_WIDTH: [GS, 0x21, 0x10],
    SIZE_DOUBLE: [GS, 0x21, 0x11],
    LINE_FEED: [LF],
    FEED_LINES: (n) => [ESC, 0x64, n],
    CUT: [GS, 0x56, 0x41, 0x03],
};

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// ─── USB Connection Manager ──────────────────────────────────────────────────

class ThermalPrinterService {
    constructor() {
        this.device = null;
        this.interfaceNumber = null;
        this.endpointOut = null;
    }

    isSupported() {
        return !!navigator.usb;
    }

    async connect() {
        if (!this.isSupported()) {
            throw new Error("WebUSB is not supported in this browser. Please use Chrome or Edge.");
        }

        try {
            const devices = await navigator.usb.getDevices();
            if (devices.length > 0) {
                this.device = devices[0];
            }

            if (!this.device) {
                this.device = await navigator.usb.requestDevice({
                    filters: [
                        { classCode: 7 }, // Printer class
                        { classCode: 0xFF }, // Vendor specific
                    ],
                });
            }

            try {
                await this.device.open();
            } catch (openError) {
                if (openError.name === "SecurityError" || openError.message.includes("Access denied")) {
                    throw new Error(
                        "Windows blocked access to the printer. To fix this:\n" +
                        "1. Download Zadig (zadig.akeo.ie)\n" +
                        "2. Select your printer in the dropdown\n" +
                        "3. Replace the driver with 'WinUSB'"
                    );
                }
                throw openError;
            }

            if (this.device.configuration === null) {
                await this.device.selectConfiguration(1);
            }

            // Find OUT endpoint robustly
            const interfaces = this.device.configuration?.interfaces || [];
            let foundInterface = null;
            let foundEndpoint = null;

            for (const iface of interfaces) {
                for (const alt of iface.alternates || []) {
                    const ep = alt.endpoints?.find(e => e.direction === "out");
                    if (ep) {
                        foundInterface = iface.interfaceNumber;
                        foundEndpoint = ep;
                        break;
                    }
                }
                if (foundEndpoint) break;
            }

            if (!foundEndpoint) {
                throw new Error("No output endpoint found on this USB device.");
            }

            this.interfaceNumber = foundInterface;
            await this.device.claimInterface(this.interfaceNumber);
            this.endpointOut = foundEndpoint.endpointNumber;

            return true;
        } catch (error) {
            console.error("🖨️ Failed to connect to printer:", error);
            this.device = null;
            throw error;
        }
    }

    async disconnect() {
        if (this.device) {
            try {
                await this.device.releaseInterface(this.interfaceNumber);
                await this.device.close();
            } catch (e) {
                // Ignore disconnect errors
            }
            this.device = null;
            this.interfaceNumber = null;
            this.endpointOut = null;
        }
    }

    async sendData(data) {
        if (!this.device || this.endpointOut == null) {
            throw new Error("Printer not connected.");
        }

        const uint8 = data instanceof Uint8Array ? data : new Uint8Array(data);

        // Send in small chunks with delay
        const CHUNK_SIZE = 128;
        for (let i = 0; i < uint8.length; i += CHUNK_SIZE) {
            const chunk = uint8.slice(i, i + CHUNK_SIZE);
            const result = await this.device.transferOut(this.endpointOut, chunk);
            
            if (result.status !== "ok") {
                throw new Error(`USB transfer failed with status: ${result.status}`);
            }

            if (i + CHUNK_SIZE < uint8.length) {
                await delay(5);
            }
        }
    }

    // ─── Receipt Builder Helpers ─────────────────────────────────────────────

    textToBytes(text) {
        const bytes = [];
        for (let i = 0; i < text.length; i++) {
            const code = text.charCodeAt(i);
            // Convert to single byte ASCII/CP437
            if (code <= 0xFF) {
                bytes.push(code);
            } else {
                bytes.push(0x3F); // '?'
            }
        }
        return bytes;
    }

    separator(char = "-") {
        return this.textToBytes(char.repeat(CHAR_WIDTH) + "\n");
    }

    doubleSeparator() {
        return this.textToBytes("=".repeat(CHAR_WIDTH) + "\n");
    }

    twoColumnLine(left, right, bold = false) {
        const maxLeft = CHAR_WIDTH - right.length - 1;
        const truncatedLeft = left.length > maxLeft ? left.substring(0, maxLeft) : left;
        const padding = " ".repeat(Math.max(0, CHAR_WIDTH - truncatedLeft.length - right.length));
        const line = truncatedLeft + padding + right;

        const bytes = [];
        if (bold) bytes.push(...COMMANDS.BOLD_ON);
        bytes.push(...this.textToBytes(line + "\n"));
        if (bold) bytes.push(...COMMANDS.BOLD_OFF);
        return bytes;
    }

    centeredText(text, bold = false, doubleSize = false) {
        const bytes = [];
        bytes.push(...COMMANDS.ALIGN_CENTER);
        if (bold) bytes.push(...COMMANDS.BOLD_ON);
        if (doubleSize) bytes.push(...COMMANDS.SIZE_DOUBLE);
        bytes.push(...this.textToBytes(text + "\n"));
        if (doubleSize) bytes.push(...COMMANDS.SIZE_NORMAL);
        if (bold) bytes.push(...COMMANDS.BOLD_OFF);
        bytes.push(...COMMANDS.ALIGN_LEFT); // Reset alignment
        return bytes;
    }

    leftText(text, bold = false) {
        const bytes = [];
        if (bold) bytes.push(...COMMANDS.BOLD_ON);
        bytes.push(...this.textToBytes(text + "\n"));
        if (bold) bytes.push(...COMMANDS.BOLD_OFF);
        return bytes;
    }

    /**
     * Build ESC/POS QR code commands by converting the QR grid into a raster bit image.
     * This ensures compatibility with all thermal printers, even those lacking native QR support.
     * @param {string} url - The URL to encode
     * @param {number} moduleSize - QR module size (scale multiplier)
     */
    qrCode(url, moduleSize = 6) {
        const qr = QRCode.create(url, { errorCorrectionLevel: 'M' });
        const size = qr.modules.size;
        const data = qr.modules.data;
        
        // Scaled dimensions
        const scaledSize = size * moduleSize;
        const widthBytes = Math.ceil(scaledSize / 8);
        
        const bytes = [];
        
        // Center the image. 80mm paper = ~512 pixels wide.
        const PAPER_WIDTH_PIXELS = 512;
        const leftPaddingPixels = Math.max(0, Math.floor((PAPER_WIDTH_PIXELS - scaledSize) / 2));
        const leftPaddingBytes = Math.floor(leftPaddingPixels / 8);
        const totalWidthBytes = widthBytes + leftPaddingBytes;
        
        bytes.push(0x1D, 0x76, 0x30, 0x00); // GS v 0 0 (Raster bit image)
        bytes.push(totalWidthBytes & 0xFF, (totalWidthBytes >> 8) & 0xFF); // xL, xH (width in bytes)
        bytes.push(scaledSize & 0xFF, (scaledSize >> 8) & 0xFF); // yL, yH (height in dots)
        
        for (let y = 0; y < size; y++) {
            // Repeat each row `moduleSize` times
            for (let r = 0; r < moduleSize; r++) {
                // Add left padding
                for (let p = 0; p < leftPaddingBytes; p++) {
                    bytes.push(0x00);
                }
                
                let currentByte = 0;
                let bitCount = 0;
                
                for (let x = 0; x < size; x++) {
                    const isDark = data[y * size + x];
                    // Repeat each pixel `moduleSize` times
                    for (let c = 0; c < moduleSize; c++) {
                        if (isDark) {
                            currentByte |= (1 << (7 - bitCount));
                        }
                        bitCount++;
                        if (bitCount === 8) {
                            bytes.push(currentByte);
                            currentByte = 0;
                            bitCount = 0;
                        }
                    }
                }
                if (bitCount > 0) {
                    bytes.push(currentByte);
                }
            }
        }
        
        return bytes;
    }

    /**
     * Derive the ordering website URL from order source metadata
     */
    getOrderSourceUrl(rawOrderData) {
        // Check for explicit source URL in order data
        if (rawOrderData?.integrations?.meta?.sourceUrl) {
            return rawOrderData.integrations.meta.sourceUrl;
        }
        if (rawOrderData?.store?.websiteUrl) {
            return rawOrderData.store.websiteUrl;
        }
        // Build from order source slug
        const source = rawOrderData?.integrations?.orderSource || rawOrderData?.orderSource;
        if (source && source !== 'N/A' && source.toLowerCase() !== 'foodie') {
            // Convert camelCase/slug to domain-friendly format
            const slug = source.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
            return `https://${slug}.flowdish.ca`;
        }
        return 'https://flowdish.ca';
    }

    // ─── Receipt Formatting ──────────────────────────────────────────────────

    calculatePricing(rawOrderData) {
        const subtotal = rawOrderData?.pricing?.subTotal
            ? (rawOrderData.pricing.subTotal / 100).toFixed(2)
            : rawOrderData?.subTotalInCents
                ? (rawOrderData.subTotalInCents / 100).toFixed(2)
                : "0.00";

        const deliveryFee = rawOrderData?.pricing?.deliveryFee
            ? (rawOrderData.pricing.deliveryFee / 100).toFixed(2)
            : rawOrderData?.deliveryFeeInCents
                ? (rawOrderData.deliveryFeeInCents / 100).toFixed(2)
                : "0.00";

        const tax = rawOrderData?.pricing?.tax
            ? (rawOrderData.pricing.tax / 100).toFixed(2)
            : rawOrderData?.taxInCents
                ? (rawOrderData.taxInCents / 100).toFixed(2)
                : "0.00";

        const discount = rawOrderData?.pricing?.discount
            ? (rawOrderData.pricing.discount / 100).toFixed(2)
            : rawOrderData?.discountInCents
                ? (rawOrderData.discountInCents / 100).toFixed(2)
                : "0.00";

        const total = rawOrderData?.pricing?.total
            ? (rawOrderData.pricing.total / 100).toFixed(2)
            : rawOrderData?.totalAmountInCents
                ? (rawOrderData.totalAmountInCents / 100).toFixed(2)
                : "0.00";

        return { subtotal, deliveryFee, tax, discount, total };
    }

    getPaymentMethod(rawOrderData) {
        const method = rawOrderData?.payment?.method || rawOrderData?.paymentMethod;
        switch (method) {
            case "credit_card": return "Credit Card";
            case "debit_card": return "Debit Card";
            case "cash": return "Cash";
            case "cash_on_delivery": return "Cash on Delivery";
            default: return method || "N/A";
        }
    }

    formatDate(timestamp) {
        if (!timestamp) return "N/A";
        return new Date(timestamp).toLocaleDateString("en-US", {
            month: "short", day: "2-digit", year: "numeric",
        });
    }

    formatTime(timestamp) {
        if (!timestamp) return "N/A";
        return new Date(timestamp).toLocaleTimeString("en-US", {
            hour: "2-digit", minute: "2-digit", hour12: true,
        });
    }

    calculateItemPrice(item, rawItem, data) {
        if (rawItem?.lineTotalInCents) {
            return (rawItem.lineTotalInCents / 100).toFixed(2);
        }
        if (rawItem?.menuItem?.price) {
            const unitPrice = rawItem.unitPriceInCents
                ? rawItem.unitPriceInCents / 100
                : rawItem.menuItem.price;
            return (unitPrice * item.quantity).toFixed(2);
        }
        const totalPrice = parseFloat(data?.price || 0);
        const totalItems = data?.items?.reduce((sum, i) => sum + i.quantity, 0) || 1;
        const avgPrice = totalPrice / totalItems;
        return (avgPrice * item.quantity).toFixed(2);
    }

    buildReceipt(data, rawOrderData, storeName = "Restaurant", copyType = "") {
        const bytes = [];
        const pricing = this.calculatePricing(rawOrderData);

        // Initialize Printer & Defaults
        bytes.push(...COMMANDS.INIT);
        bytes.push(...COMMANDS.CODEPAGE_CP437);
        bytes.push(...COMMANDS.SIZE_NORMAL);
        bytes.push(...COMMANDS.ALIGN_LEFT);
        
        // Copy Type Header
        if (copyType) {
            bytes.push(...this.doubleSeparator());
            bytes.push(...this.centeredText(copyType, true));
            bytes.push(...this.doubleSeparator());
            bytes.push(...COMMANDS.LINE_FEED);
        }

        // Header
        bytes.push(...this.doubleSeparator());
        bytes.push(...this.centeredText(storeName.toUpperCase(), true, true));
        bytes.push(...this.doubleSeparator());

        // Order Info
        bytes.push(...this.leftText(`Order #${data?.id || "N/A"}`, true));
        bytes.push(...this.leftText(
            `Date: ${this.formatDate(data?.createdAt)}  ${this.formatTime(data?.createdAt)}`
        ));
        bytes.push(...this.separator());

        // Customer Info
        const customerName =
            rawOrderData?.integrations?.meta?.customer_f_name && rawOrderData?.integrations?.meta?.customer_l_name
                ? `${rawOrderData.integrations.meta.customer_f_name} ${rawOrderData.integrations.meta.customer_l_name}`
                : rawOrderData?.roundtripPayload?.customer_f_name && rawOrderData?.roundtripPayload?.customer_l_name
                    ? `${rawOrderData.roundtripPayload.customer_f_name} ${rawOrderData.roundtripPayload.customer_l_name}`
                    : rawOrderData?.customer?.profile?.first_name || rawOrderData?.guestInfo?.name || data?.name || "N/A";

        const customerPhone =
            rawOrderData?.integrations?.meta?.customer_phone ||
            rawOrderData?.roundtripPayload?.customer_phone ||
            rawOrderData?.customer?.profile?.phone ||
            rawOrderData?.guestInfo?.phone ||
            data?.phone || "N/A";

        bytes.push(...this.leftText(`Customer: ${customerName}`));
        bytes.push(...this.leftText(`Phone: ${customerPhone}`));

        const orderType = data?.orderType || rawOrderData?.orderType || "N/A";
        bytes.push(...this.leftText(`Type: ${orderType.charAt(0).toUpperCase() + orderType.slice(1)}`));

        if (orderType?.toLowerCase() === "delivery") {
            const address =
                rawOrderData?.fulfillment?.address?.street_address ||
                rawOrderData?.integrations?.meta?.drop_off_location ||
                rawOrderData?.roundtripPayload?.drop_off_location ||
                data?.address || "";
            if (address && address !== "N/A") {
                bytes.push(...this.leftText(`Address: ${address}`));
            }
        }

        bytes.push(...COMMANDS.LINE_FEED);

        // Items Section
        bytes.push(...this.separator());
        bytes.push(...this.leftText("ITEMS", true));
        bytes.push(...this.separator());

        if (data?.items && data.items.length > 0) {
            data.items.forEach((item, index) => {
                const rawItem = rawOrderData?.items?.[index];
                const price = this.calculateItemPrice(item, rawItem, data);
                const itemName = `${item.title || "Item"} x ${item.quantity}`;
                bytes.push(...this.twoColumnLine(itemName, `$${price}`));

                if (item.addOn) {
                    bytes.push(...this.leftText(`  > ${item.addOn}`));
                }
            });
        }

        bytes.push(...COMMANDS.LINE_FEED);

        // Special Instructions
        const specialInstructions = rawOrderData?.notes ||
            rawOrderData?.items?.filter(item => item.notes).map(item => item.notes).join(", ");
        if (specialInstructions) {
            bytes.push(...this.separator());
            bytes.push(...this.leftText("Special Instructions:", true));
            bytes.push(...this.leftText(specialInstructions));
            bytes.push(...COMMANDS.LINE_FEED);
        }

        // Pricing Section
        bytes.push(...this.separator());
        bytes.push(...this.twoColumnLine("Subtotal", `$${pricing.subtotal}`));

        if (orderType?.toLowerCase() === "delivery" && parseFloat(pricing.deliveryFee) > 0) {
            bytes.push(...this.twoColumnLine("Delivery Fee", `$${pricing.deliveryFee}`));
        }

        if (parseFloat(pricing.tax) > 0) {
            bytes.push(...this.twoColumnLine("Tax", `$${pricing.tax}`));
        }

        if (parseFloat(pricing.discount) > 0) {
            bytes.push(...this.twoColumnLine("Discount", `-$${pricing.discount}`));
        }

        bytes.push(...this.separator());
        bytes.push(...this.twoColumnLine("TOTAL", `$${pricing.total}`, true));
        bytes.push(...COMMANDS.LINE_FEED);

        // Payment Method
        const paymentMethod = this.getPaymentMethod(rawOrderData);
        bytes.push(...this.leftText(`Payment: ${paymentMethod}`));

        if ((rawOrderData?.payment?.status === "cod_pending" || rawOrderData?.paymentStatus === "cod_pending") && data?.status?.toLowerCase() !== "delivered") {
            bytes.push(...this.leftText("Status: Cash Pending"));
        } else if ((rawOrderData?.payment?.status === "collected" || rawOrderData?.paymentStatus === "collected") || ((rawOrderData?.payment?.status === "cod_pending" || rawOrderData?.paymentStatus === "cod_pending") && data?.status?.toLowerCase() === "delivered")) {
            bytes.push(...this.leftText("Status: Cash Collected"));
        }

        // Pickup Code
        if (data?.pickupCode && data.pickupCode !== "N/A") {
            bytes.push(...COMMANDS.LINE_FEED);
            bytes.push(...this.separator());
            bytes.push(...this.centeredText("PICKUP CODE", true));
            bytes.push(...this.centeredText(data.pickupCode, true, true));
            bytes.push(...this.separator());
        }

        // QR Code (Customer Copy only)
        if (copyType === "CUSTOMER COPY" || !copyType) {
            const qrUrl = this.getOrderSourceUrl(rawOrderData);
            bytes.push(...COMMANDS.LINE_FEED);
            bytes.push(...this.separator());
            bytes.push(...this.centeredText("SCAN TO ORDER AGAIN", true));
            bytes.push(...COMMANDS.LINE_FEED);
            bytes.push(...this.qrCode(qrUrl));
            bytes.push(...COMMANDS.LINE_FEED);
            bytes.push(...this.separator());
        }

        // Footer
        bytes.push(...COMMANDS.LINE_FEED);
        bytes.push(...this.doubleSeparator());
        bytes.push(...this.centeredText("Thank you for your"));
        bytes.push(...this.centeredText("order!"));
        bytes.push(...this.doubleSeparator());

        // Feed and cut
        bytes.push(...COMMANDS.FEED_LINES(4));
        bytes.push(...COMMANDS.CUT);

        return new Uint8Array(bytes);
    }

    async printReceipt(data, rawOrderData, storeName = "Restaurant") {
        if (!this.isSupported()) {
            throw new Error("WebUSB is not supported. Please use Google Chrome or Microsoft Edge.");
        }

        const copiesSetting = localStorage.getItem("foodie_receipt_copies") || "both";

        try {
            await this.connect();

            // Send soft reset to clear printer buffer before printing
            try {
                await this.device.controlTransferOut({
                    requestType: "class", recipient: "interface", request: 0x02, value: 0, index: this.interfaceNumber,
                });
                await delay(50);
            } catch (ignored) {}

            let printedCount = 0;

            // Print Restaurant Copy
            if (copiesSetting === "both" || copiesSetting === "restaurant") {
                const restaurantReceipt = this.buildReceipt(data, rawOrderData, storeName, "RESTAURANT COPY");
                console.log(`🖨️ Sending RESTAURANT COPY (${restaurantReceipt.length} bytes)...`);
                await this.sendData(restaurantReceipt);
                printedCount++;
            }
            
            // Print Customer Copy
            if (copiesSetting === "both" || copiesSetting === "customer") {
                if (printedCount > 0) await delay(1000); // Wait 1 second between prints to avoid overlap
                
                const customerReceipt = this.buildReceipt(data, rawOrderData, storeName, "CUSTOMER COPY");
                console.log(`🖨️ Sending CUSTOMER COPY (${customerReceipt.length} bytes)...`);
                await this.sendData(customerReceipt);
                printedCount++;
            }
            
            const msg = printedCount === 2 
                ? "Both receipts printed successfully!" 
                : `${copiesSetting === "restaurant" ? "Restaurant" : "Customer"} receipt printed successfully!`;
                
            console.log(`🖨️ ✅ ${msg}`);
            return { success: true, message: msg };
        } catch (error) {
            console.error("🖨️ Print error:", error);
            if (error.message?.includes("No device selected")) {
                throw new Error("No printer was selected. Please select your thermal printer when prompted.");
            }
            if (error.message?.includes("Unable to claim interface")) {
                throw new Error("The printer is busy. Please close other programs using it and try again.");
            }
            throw error;
        } finally {
            await this.disconnect();
        }
    }
}

const thermalPrinterService = new ThermalPrinterService();
export default thermalPrinterService;
